/* Geschenke-Shop – Chat-Helfer.
   Stufe 1 (immer): Antworten aus der Wissensbasis im Browser – Suchwörter mit Gewicht,
   Wortanfänge und ein Tippfehler je Wort, Vorrang für Themen der aktuellen Seite.
   Stufe 2 (nur mit eingetragenem KI-Endpunkt): unbeantwortete Fragen ohne Kontaktdaten
   an den Endpunkt (chatbot/worker.js). Nutzertext wird nie als HTML eingesetzt. */
(() => {
  if (window.customElements.get('gs-chat')) return;

  const STORE = 'gs-chat';
  const MAX_MSGS = 40;
  const THRESHOLD = 2;
  const motionOk = () =>
    window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

  /* ---------- Text aufbereiten ---------- */

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  /* Levenshtein bis höchstens 1 – reicht für einen Tippfehler je Wort */
  const nearlyEqual = (a, b) => {
    if (a === b) return true;
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) {
        i++;
        j++;
        continue;
      }
      if (++edits > 1) return false;
      if (la > lb) i++;
      else if (lb > la) j++;
      else {
        i++;
        j++;
      }
    }
    return edits + (la - i) + (lb - j) <= 1;
  };

  const wordMatch = (token, key) => {
    if (token === key) return 1;
    if (key.length >= 4 && token.startsWith(key)) return 1;
    if (key.length >= 5 && token.length >= key.length - 1) {
      const head = token.slice(0, key.length);
      if (nearlyEqual(head, key) || nearlyEqual(token, key)) return 0.75;
    }
    return 0;
  };

  const PII = [
    /[^\s@]+@[^\s@]+\.[a-z]{2,}/i,
    /(\+|00)?\d[\d\s/().-]{7,}\d/,
    /#\s?\d{4,}/,
    /\b\d{5}\s+[A-ZÄÖÜ][a-zäöüß]+/,
  ];
  const hasPII = (s) => PII.some((re) => re.test(s));
  const stripPII = (s) =>
    PII.reduce((t, re) => t.replace(new RegExp(re.source, re.flags + 'g'), '[entfernt]'), s);

  const offenMarkup = (html) =>
    html.replace(/\[OFFEN:([^\]]*)\]/g, '<span class="gs-offen">[OFFEN:$1]</span>');

  /* ---------- Wissensbasis ---------- */

  class Wissen {
    constructor(data) {
      this.data = data;
      this.byId = new Map();
      this.intents = data.intents.map((it) => {
        const keys = it.k.map((entry) => {
          const [word, weight] = entry.split(':');
          const n = norm(word);
          return { n, w: Number(weight) || 1, phrase: n.includes(' ') };
        });
        const out = { ...it, keys, qn: norm(it.q) };
        this.byId.set(it.id, out);
        return out;
      });
    }

    rank(text) {
      const n = norm(text);
      if (!n) return [];
      const tokens = n.split(' ');
      const page = this.data.page;
      return this.intents
        .map((it) => {
          if (it.qn && it.qn === n) return { it, score: 99 };
          let score = 0;
          for (const key of it.keys) {
            if (key.phrase) {
              if (n.includes(key.n)) score += key.w * 1.2;
              continue;
            }
            let best = 0;
            for (const t of tokens) best = Math.max(best, wordMatch(t, key.n));
            score += key.w * best;
          }
          if (score > 0 && it.c && it.c === page) score += 0.5;
          return { it, score };
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score);
    }

    start() {
      const s = this.data.start;
      return (s[this.data.page] || s.default).map((id) => this.byId.get(id)).filter(Boolean);
    }
  }

  /* ---------- Element ---------- */

  class GsChat extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-gs-chat-wissen]');
      try {
        this.wissen = new Wissen(JSON.parse(raw.textContent));
      } catch (e) {
        this.hidden = true;
        return;
      }
      this.launcher = this.querySelector('[data-gs-chat-launcher]');
      this.panel = this.querySelector('.gs-chat__panel');
      this.log = this.querySelector('[data-gs-chat-log]');
      this.chips = this.querySelector('[data-gs-chat-chips]');
      this.form = this.querySelector('[data-gs-chat-form]');
      this.input = this.querySelector('[data-gs-chat-input]');
      this.t = this.dataset;
      this.misses = 0;
      this.busy = false;
      this.hits = new Set();
      this.state = this.#load();

      this.launcher.addEventListener('click', () => this.toggle());
      this.querySelector('[data-gs-chat-close]').addEventListener('click', () => this.toggle(false));
      this.querySelector('[data-gs-chat-clear]').addEventListener('click', () => this.clear());
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.ask(this.input.value);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) {
          e.stopPropagation();
          this.toggle(false);
        }
      });
      this.log.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-intent]');
        if (chip) this.ask(chip.textContent, chip.dataset.intent);
      });
      this.chips.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-intent]');
        if (chip) this.ask(chip.textContent, chip.dataset.intent);
      });

      this.#render();
      this.#avoidBuyButtons();
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-gs-chat-open]')) this.toggle(true);
      });

      const wide = window.matchMedia('(min-width: 750px)').matches;
      if (this.dataset.pendingOpen || (this.state.open && wide)) {
        delete this.dataset.pendingOpen;
        this.toggle(true);
      }
    }

    /* ---------- Öffnen / Schließen ---------- */

    toggle(force) {
      const open = typeof force === 'boolean' ? force : this.panel.hidden;
      if (open === !this.panel.hidden) return;
      this.launcher.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      this.classList.remove('is-yielding');
      this.state.open = open;
      this.#save();

      if (open) {
        this.panel.hidden = false;
        if (!this.state.msgs.length) this.#greet();
        this.#animate(this.panel, true);
        const lastBot = [...this.log.querySelectorAll('.gs-chat__msg--bot')].pop();
        if (lastBot) this.log.scrollTop = Math.max(0, lastBot.offsetTop - 16);
        const touch = window.matchMedia('(pointer: coarse)').matches;
        /* Am Handy nicht sofort die Tastatur aufklappen */
        (touch ? this.panel.querySelector('.gs-chat__title') : this.input).focus({ preventScroll: true });
      } else {
        const done = () => {
          this.panel.hidden = true;
          if (document.activeElement !== this.launcher) this.#yield();
        };
        this.#animate(this.panel, false).then(done);
        if (this.contains(document.activeElement) || document.activeElement === document.body) {
          this.launcher.focus({ preventScroll: true });
          this.launcher.addEventListener('blur', () => this.#yield(), { once: true });
        }
      }
    }

    #animate(el, show) {
      if (!motionOk() || this.dataset.motion === 'false' || !el.animate) return Promise.resolve();
      const frames = [
        { opacity: 0, transform: 'translateY(12px) scale(0.98)' },
        { opacity: 1, transform: 'none' },
      ];
      const anim = el.animate(show ? frames : frames.reverse(), {
        duration: show ? 240 : 160,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      });
      return anim.finished.catch(() => {});
    }

    /* ---------- Fragen und Antworten ---------- */

    async ask(text, intentId) {
      const q = String(text || '').trim().slice(0, 300);
      if (!q || this.busy) return;
      this.input.value = '';
      this.#push({ r: 'u', t: q });

      if (!intentId && hasPII(q)) {
        this.#push({ r: 'b', t: this.wissen.data.privacyWarn, warn: true });
      }

      if (intentId && this.wissen.byId.has(intentId)) {
        await this.#typing(350);
        this.misses = 0;
        return this.#push({ r: 'b', id: intentId });
      }

      const ranked = this.wissen.rank(q);
      const [best, second] = ranked;
      if (best && best.score >= THRESHOLD) {
        await this.#typing(350);
        this.misses = 0;
        const alt = second && second.score >= best.score * 0.8 ? second.it.id : null;
        return this.#push({ r: 'b', id: best.it.id, alt });
      }

      if (this.wissen.data.endpoint) {
        const answer = await this.#askAI(q);
        if (answer) {
          this.misses = 0;
          return this.#push({ r: 'b', t: answer, ai: true });
        }
      } else {
        await this.#typing(350);
      }

      this.misses += 1;
      const guesses = ranked.slice(0, 2).map((r) => r.it.id);
      this.#push({ r: 'b', fb: this.misses > 1 ? 2 : 1, guesses });
    }

    async #askAI(q) {
      this.busy = true;
      const stop = this.#showTyping();
      try {
        const history = this.state.msgs
          .slice(-6)
          .filter((m) => m.t && !m.warn)
          .map((m) => ({ r: m.r, t: stripPII(m.t).slice(0, 300) }));
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), 12000);
        const res = await fetch(this.wissen.data.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ frage: stripPII(q), seite: this.wissen.data.page, verlauf: history }),
          signal: ctrl.signal,
          credentials: 'omit',
        });
        window.clearTimeout(timer);
        if (!res.ok) return null;
        const data = await res.json();
        return typeof data.antwort === 'string' && data.antwort.trim() ? data.antwort.trim().slice(0, 1200) : null;
      } catch (e) {
        return null;
      } finally {
        stop();
        this.busy = false;
      }
    }

    #typing(ms) {
      if (!motionOk() || this.dataset.motion === 'false') return Promise.resolve();
      this.busy = true;
      const stop = this.#showTyping();
      return new Promise((resolve) =>
        window.setTimeout(() => {
          stop();
          this.busy = false;
          resolve();
        }, ms)
      );
    }

    #showTyping() {
      const el = document.createElement('div');
      el.className = 'gs-chat__typing';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<span></span><span></span><span></span>';
      const sr = document.createElement('p');
      sr.className = 'gs-sr-only';
      sr.textContent = this.t.tTyping;
      this.log.append(el, sr);
      this.panel.setAttribute('aria-busy', 'true');
      this.#scrollDown();
      return () => {
        el.remove();
        sr.remove();
        this.panel.removeAttribute('aria-busy');
      };
    }

    greetingMsg() {
      return { r: 'b', t: this.wissen.data.greeting, start: true };
    }

    #greet() {
      this.#push(this.greetingMsg());
    }

    clear() {
      this.state.msgs = [];
      this.misses = 0;
      this.log.textContent = '';
      this.#greet();
      this.input.focus({ preventScroll: true });
    }

    /* ---------- Darstellung ---------- */

    #push(msg) {
      this.state.msgs.push(msg);
      if (this.state.msgs.length > MAX_MSGS) this.state.msgs = this.state.msgs.slice(-MAX_MSGS);
      this.#save();
      const el = this.#bubble(msg, true);
      this.log.append(el);
      this.#setChips(msg);
      /* Antworten ab ihrem Anfang zeigen, nicht ab dem Ende */
      if (msg.r === 'b') this.log.scrollTop = Math.max(0, el.offsetTop - 16);
      else this.#scrollDown();
    }

    #render() {
      this.log.textContent = '';
      this.state.msgs.forEach((m) => this.log.append(this.#bubble(m, false)));
      const last = [...this.state.msgs].reverse().find((m) => m.r === 'b');
      this.#setChips(last || { start: true });
    }

    #bubble(msg, fresh) {
      const wrap = document.createElement('div');
      wrap.className = `gs-chat__msg gs-chat__msg--${msg.r === 'u' ? 'user' : 'bot'}`;
      const who = document.createElement('span');
      who.className = 'gs-sr-only';
      who.textContent = (msg.r === 'u' ? this.t.tYou : this.t.tBot) + ': ';
      const body = document.createElement('div');
      body.className = 'gs-chat__bubble';
      body.append(who);

      if (msg.r === 'u' || msg.ai || (msg.t && !msg.id && !msg.fb)) {
        const p = document.createElement('p');
        p.textContent = msg.t;
        body.append(p);
        if (msg.ai) {
          const tag = document.createElement('p');
          tag.className = 'gs-chat__tag';
          tag.textContent = this.t.tAi;
          body.append(tag);
        }
      } else if (msg.id) {
        const it = this.wissen.byId.get(msg.id);
        const p = document.createElement('div');
        p.innerHTML = offenMarkup(it ? it.a : '');
        this.#fillMail(p);
        body.append(p);
        const follow = it ? it.f : [];
        if (msg.alt && !follow.includes(msg.alt) && this.wissen.byId.get(msg.alt)?.q) {
          const alt = document.createElement('p');
          alt.className = 'gs-chat__alt';
          alt.textContent = `${this.t.tMean} `;
          alt.append(this.#chip(this.wissen.byId.get(msg.alt)));
          body.append(alt);
        }
      } else if (msg.fb) {
        const p = document.createElement('p');
        p.textContent = msg.fb > 1 ? this.t.tAgain : this.wissen.data.fallback;
        body.append(p);
      }

      wrap.append(body);
      if (fresh && msg.r === 'b' && motionOk() && this.dataset.motion !== 'false' && wrap.animate) {
        requestAnimationFrame(() =>
          wrap.animate(
            [
              { opacity: 0, transform: 'translateY(6px)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          )
        );
      }
      return wrap;
    }

    /* Übergabe an Menschen: bisherige Fragen in die E-Mail vorausfüllen –
       verschickt wird erst im eigenen Mailprogramm */
    #fillMail(root) {
      const asked = this.state.msgs
        .filter((m) => m.r === 'u')
        .slice(-5)
        .map((m) => `– ${m.t}`)
        .join('\n');
      if (!asked) return;
      root.querySelectorAll('a[href^="mailto:"]').forEach((a) => {
        const text = `Guten Tag,\n\n\n\nMeine Fragen an den Chat-Helfer:\n${asked}`;
        a.href = `${a.getAttribute('href').split('&body=')[0]}&body=${encodeURIComponent(text)}`;
      });
    }

    #chip(it) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gs-chat__chip';
      b.dataset.intent = it.id;
      b.textContent = it.q;
      return b;
    }

    #setChips(msg) {
      const w = this.wissen;
      let list = [];
      if (msg.start) list = w.start();
      else if (msg.id) list = (w.byId.get(msg.id)?.f || []).map((id) => w.byId.get(id));
      else if (msg.fb) {
        const ids = msg.fb > 1 ? ['mensch'] : [...(msg.guesses || []), 'mensch'];
        list = [...new Set(ids)].map((id) => w.byId.get(id));
        if (list.length < 3) list = [...list, ...w.start()].filter((v, i, a) => a.indexOf(v) === i);
      } else if (msg.ai || msg.warn) list = [w.byId.get('mensch')];
      this.chips.textContent = '';
      list
        .filter((it) => it && it.q)
        .slice(0, 4)
        .forEach((it) => this.chips.append(this.#chip(it)));
    }

    #scrollDown() {
      this.log.scrollTop = this.log.scrollHeight;
    }

    /* ---------- Kaufknöpfe nie verdecken ---------- */

    #yield() {
      const focused = document.activeElement === this.launcher;
      this.classList.toggle('is-yielding', this.hits.size > 0 && this.panel.hidden && !focused);
    }

    #avoidBuyButtons() {
      const selectors =
        '.add-to-cart-button, .gs-product__cta, .cart__checkout-button, .gs-legal-footer';
      const targets = [...document.querySelectorAll(selectors)];
      const hits = this.hits;
      let io = null;
      const setup = () => {
        if (io) io.disconnect();
        const strip = 112;
        io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => (e.isIntersecting ? hits.add(e.target) : hits.delete(e.target)));
            this.#yield();
          },
          { rootMargin: `-${Math.max(0, window.innerHeight - strip)}px 0px 0px 0px` }
        );
        targets.forEach((t) => io.observe(t));
      };
      if (targets.length && 'IntersectionObserver' in window) {
        setup();
        let timer = 0;
        window.addEventListener('resize', () => {
          window.clearTimeout(timer);
          timer = window.setTimeout(setup, 200);
        });
      }

      /* Kaufleiste am Handy: Knopf darüber schieben */
      const bar = document.querySelector('[data-gs-sticky]');
      if (bar) {
        const sync = () => {
          const up = bar.classList.contains('is-visible') ? bar.offsetHeight : 0;
          this.style.setProperty('--gs-chat-lift', `${up}px`);
        };
        new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['class'] });
        sync();
      }
    }

    /* ---------- Verlauf nur für diesen Tab ---------- */

    #load() {
      try {
        const s = JSON.parse(window.sessionStorage.getItem(STORE) || '{}');
        return { open: !!s.open, msgs: Array.isArray(s.msgs) ? s.msgs : [] };
      } catch (e) {
        return { open: false, msgs: [] };
      }
    }

    #save() {
      try {
        window.sessionStorage.setItem(STORE, JSON.stringify(this.state));
      } catch (e) {
        /* Speicher gesperrt: Verlauf gilt nur bis zum Seitenwechsel */
      }
    }
  }

  window.customElements.define('gs-chat', GsChat);
})();
